import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  useTheme,
  alpha,
  Button,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MdCelebration, MdEvent, MdArrowForward } from "react-icons/md";
import { getSecureLocalItem } from "utils/secureSessionStorage";
import { fetchHolidays } from "store/features/company/leaveManagement/holiday/holidayActions";
import {
  selectHolidays,
  selectHolidayLoading,
} from "store/features/company/leaveManagement/holiday/holidaySelectors";

const UpcomingHolidaysWidget = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";

  const rawHolidays = useSelector(selectHolidays);
  const holidays = useMemo(() => rawHolidays || [], [rawHolidays]);
  const loading = useSelector(selectHolidayLoading);

  useEffect(() => {
    const token = getSecureLocalItem("token");
    if (!token) return;
    dispatch(fetchHolidays()).catch(() => { });
  }, [dispatch]);

  // Process & filter upcoming holidays
  const upcomingHolidays = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return [...holidays]
      .filter((h) => {
        if (!h.holiday_date) return false;
        const hDate = new Date(h.holiday_date);
        hDate.setHours(0, 0, 0, 0);
        return hDate >= today;
      })
      .sort((a, b) => new Date(a.holiday_date) - new Date(b.holiday_date))
      .slice(0, 4);
  }, [holidays]);

  // Fallback: If no future holidays exist in the database, show closest sorted list
  const displayHolidays = upcomingHolidays.length > 0
    ? upcomingHolidays
    : [...holidays].slice(0, 4);

  const getDaysRemainingText = (dateString) => {
    if (!dateString) return "";
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(dateString);
    targetDate.setHours(0, 0, 0, 0);

    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today! 🎉";
    if (diffDays === 1) return "Tomorrow";
    if (diffDays > 1) return `In ${diffDays} days`;
    return "";
  };

  const formatDateDetails = (dateString) => {
    if (!dateString) return { month: "-", day: "-", weekday: "-" };
    const date = new Date(dateString);
    return {
      month: date.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
      day: date.getDate(),
      weekday: date.toLocaleDateString("en-US", { weekday: "short" }),
    };
  };

  return (
    <Paper
      component={motion.div}
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 1,
        background: isDark ? "#111827" : "#ffffff",
        border: 1, borderColor: "divider",
        boxShadow: isDark
          ? "0 4px 20px rgba(0, 0, 0, 0.25)"
          : "0 4px 20px rgba(15, 23, 42, 0.05)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
          <Stack direction="row" spacing={1.5} sx={{display: 'flex', justifyContent: 'space-between'}}>
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
                bgcolor: isDark ? alpha("#f59e0b", 0.2) : "#fef3c7",
                color: "#d97706",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MdCelebration size={20} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary" }}>
              Upcoming Holidays
            </Typography>
          </Stack>

          <Button
            size="small"
            endIcon={<MdArrowForward />}
            onClick={() => navigate("/company/holiday")}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: "primary.main",
            }}
          >
            View All ({holidays.length})
          </Button>
        </Stack>

        {/* Holidays Content */}
        {loading ? (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} thickness={5} sx={{ color: "primary.main" }} />
          </Box>
        ) : displayHolidays.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No upcoming holidays scheduled.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.8}>
            {displayHolidays.map((holiday, idx) => {
              const { month, day, weekday } = formatDateDetails(holiday.holiday_date);
              const daysRemaining = getDaysRemainingText(holiday.holiday_date);

              return (
                <Box
                  key={holiday.id || idx}
                  sx={{
                    p: 1.8,
                    borderRadius: 1,
                    bgcolor: "background.paper",
                    border: 1, borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                  }}
                >
                  {/* Left: Date Badge & Name */}
                  <Stack direction="row" spacing={1.8} alignItems="center">
                    {/* Date Calendar Badge */}
                    <Box
                      sx={{
                        width: 44,
                        height: 48,
                        borderRadius: 1,
                        bgcolor: isDark ? alpha("#f59e0b", 0.15) : "#fef3c7",
                        border: `1px solid ${isDark ? alpha("#f59e0b", 0.3) : "#fde68a"}`,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.62rem",
                          fontWeight: 800,
                          color: "#d97706",
                          lineHeight: 1,
                          letterSpacing: "0.05em",
                        }}
                      >
                        {month}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 800,
                          color: "text.primary",
                          lineHeight: 1.1,
                          mt: 0.2,
                        }}
                      >
                        {day}
                      </Typography>
                    </Box>

                    {/* Holiday Title & Subtext */}
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: "text.primary",
                          lineHeight: 1.2,
                        }}
                      >
                        {holiday.holiday_name || holiday.name || "Company Holiday"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 0.3,
                        }}
                      >
                        <MdEvent size={12} color="#f59e0b" /> {weekday}
                        {holiday.holiday_type && ` • ${holiday.holiday_type}`}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Right: Days Remaining Chip */}
                  {daysRemaining && (
                    <Chip
                      label={daysRemaining}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        backgroundColor: isDark ? alpha("#10b981", 0.15) : "#d1fae5",
                        color: "#059669",
                        borderRadius: "6px",
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      <Button
        variant="outlined"
        fullWidth
        endIcon={<MdArrowForward />}
        onClick={() => navigate("/company/holiday")}
        sx={{
          mt: 2,
          borderRadius: 1,
          textTransform: "none",
          fontWeight: 700,
          borderColor: isDark ? "rgba(255,255,255,0.15)" : "#cbd5e1",
          color: isDark ? "#e2e8f0" : "#334155",
          "&:hover": {
            borderColor: "#f59e0b",
            backgroundColor: alpha("#f59e0b", 0.08),
          },
        }}
      >
        View Full Holiday Calendar
      </Button>
    </Paper>
  );
};

export default UpcomingHolidaysWidget;
